import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../services/baseurl";
import { getAllBatchesEndPoint } from "../services/endpoints";
import moment from "moment";
import shared from "../css/createBatchDesign.module.css";
import styles from "../css/collectionsDesign.module.css";

const statuses = { A: "Active", P: "Pending", C: "Completed", D: "Deleted" };
const filters = [["all", "All collections"], ...Object.entries(statuses)];
const money = (value) =>
  value == null
    ? "Not set"
    : new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
      }).format(Number(value) || 0);
const videoUrl = (value) => {
  try {
    const url = new URL(value);
    return ["http:", "https:"].includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
};

function CollectionImage({ sources, alt, cover = false }) {
  const [index, setIndex] = useState(0);
  const source = sources[index];
  return source ? (
    <img
      src={source}
      alt={alt}
      loading="lazy"
      onError={() => setIndex((previous) => previous + 1)}
    />
  ) : (
    <div
      className={`${styles.imageFallback} ${cover ? styles.coverFallback : ""}`}
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.3"
        aria-hidden="true"
      >
        <rect x="3" y="3" width="18" height="18" rx="4" />
        <path d="m3 17 6-6 5 5 3-3 4 4" />
        <circle cx="16" cy="8" r="1.5" />
      </svg>
      <span>{cover ? "Collection preview" : "Preview unavailable"}</span>
    </div>
  );
}

function CollectionCard({ batch }) {
  const images = Array.isArray(batch.images)
    ? batch.images.filter((image) => image?.imageURL)
    : [];
  const sources = [
    ...new Set(
      [batch.phoneId, ...images.map((image) => image.imageURL)].filter(Boolean),
    ),
  ];
  const video = videoUrl(batch.winnerURL);
  const drawDate =
    batch.drawDate && moment(batch.drawDate).isValid()
      ? moment(batch.drawDate).format("DD MMM YYYY")
      : "Not scheduled";
  return (
    <article
      className={styles.card}
      aria-labelledby={`batch-title-${batch.batchId}`}
    >
      <div className={styles.cover}>
        <CollectionImage
          key={sources.join("|")}
          sources={sources}
          alt={
            batch.phoneName ||
            images[0]?.imageName ||
            `Batch ${batch.batchId} preview`
          }
          cover
        />
        <span className={styles.coverTag}>
          {images.length} {images.length === 1 ? "image" : "images"}
        </span>
      </div>
      <div className={styles.cardContent}>
        <header className={styles.cardHeader}>
          <div>
            <span className={shared.eyebrow}>IMAGE COLLECTION</span>
            <h2 id={`batch-title-${batch.batchId}`}>Batch #{batch.batchId}</h2>
          </div>
          <span
            className={`${styles.statusBadge} ${styles[`status${batch.status}`] || ""}`}
          >
            <span />
            {statuses[batch.status] || "Unknown"}
          </span>
        </header>
        <dl className={styles.facts}>
          <div>
            <dt>Token price</dt>
            <dd>{money(batch.amount)}</dd>
          </div>
          <div>
            <dt>Draw date</dt>
            <dd>{drawDate}</dd>
          </div>
          <div>
            <dt>Winner ID</dt>
            <dd>
              {Number(batch.winnerId) > 0
                ? `#${batch.winnerId}`
                : "Not selected"}
            </dd>
          </div>
        </dl>
        {images.length > 0 ? (
          <details className={styles.gallery}>
            <summary>
              <span>
                Inside this collection{" "}
                <small>
                  {images.length} {images.length === 1 ? "image" : "images"}
                </small>
              </span>
              <span className={styles.chevron} aria-hidden="true">
                &#8964;
              </span>
            </summary>
            <div className={styles.galleryGrid}>
              {images.map((image, index) => (
                <figure key={`${image.imageId}-${index}`}>
                  <div>
                    <CollectionImage
                      key={image.imageURL}
                      sources={[image.imageURL]}
                      alt={image.imageName || `Collection item ${index + 1}`}
                    />
                  </div>
                  <figcaption title={image.imageName}>
                    {image.imageName || `Item ${index + 1}`}
                  </figcaption>
                </figure>
              ))}
            </div>
          </details>
        ) : (
          <p className={styles.noImages}>No collection images added yet.</p>
        )}
        <footer className={styles.cardFooter}>
          <Link to="/transactions" state={{ id: batch.batchId }}>
            View transactions <span aria-hidden="true">&#8599;</span>
          </Link>
          {video ? (
            <a href={video} target="_blank" rel="noopener noreferrer">
              Watch winner video <span aria-hidden="true">&#8599;</span>
            </a>
          ) : (
            <span className={styles.noVideo}>Winner video not available</span>
          )}
        </footer>
      </div>
    </article>
  );
}

export default function Uploads() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(6);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    axiosInstance
      .get(getAllBatchesEndPoint)
      .then((response) => {
        if (
          response.data.status !== "Success" ||
          !Array.isArray(response.data.payload)
        )
          throw new Error(
            response.data.message || "Unable to load collections.",
          );
        if (!cancelled)
          setBatches(
            [...response.data.payload]
              .filter(Boolean)
              .sort((a, b) => Number(b.batchId) - Number(a.batchId)),
          );
      })
      .catch((requestError) => {
        if (!cancelled)
          setError(
            requestError.response?.data?.message ||
              requestError.message ||
              "Unable to load collections.",
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [retry]);
  const matching = batches.filter(
    (batch) =>
      (filter === "all" || batch.status === filter) &&
      [
        batch.batchId,
        batch.phoneName,
        ...(Array.isArray(batch.images)
          ? batch.images.map((image) => image?.imageName)
          : []),
      ].some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(search.trim().toLowerCase()),
      ),
  );
  return (
    <div className="dashboard-glass admin-theme">
      <main className={styles.workspace}>
        <header className={shared.pageHeader}>
          <div>
            <span className={shared.eyebrow}>YOUR COLLECTIONS</span>
            <h1>All batch details</h1>
            <p>
              Every collection, thoughtfully organized. Find a batch and explore
              what is inside.
            </p>
          </div>
          <Link
            to="/crateBatch"
            className={`${shared.primaryButton} ${styles.createLink}`}
          >
            Create a batch <span aria-hidden="true">&#8599;</span>
          </Link>
        </header>
        <section
          className={styles.collectionIntro}
          aria-label="Collection overview"
        >
          <div>
            <span className={shared.eyebrow}>COLLECTION LIBRARY</span>
            <h2>Your images. Your collections.</h2>
            <p>
              Keep track of active draws, upcoming batches, and completed
              collections in one place.
            </p>
          </div>
          <div className={styles.overviewCounts}>
            <div>
              <strong>{loading || error ? "--" : batches.length}</strong>
              <span>Total batches</span>
            </div>
            <div>
              <strong>
                {loading || error
                  ? "--"
                  : batches.filter((batch) => batch.status === "A").length}
              </strong>
              <span>Active now</span>
            </div>
            <div>
              <strong>
                {loading || error
                  ? "--"
                  : batches.filter((batch) => batch.status === "P").length}
              </strong>
              <span>Upcoming</span>
            </div>
          </div>
        </section>
        <div className={styles.toolbar}>
          <div
            className={styles.filters}
            role="group"
            aria-label="Filter by batch status"
          >
            {filters.map(([value, label]) => (
              <button
                type="button"
                key={value}
                aria-pressed={filter === value}
                className={`${styles.filterButton} ${filter === value ? styles.selected : ""}`}
                onClick={() => {
                  setFilter(value);
                  setLimit(6);
                }}
              >
                {label}
                <span>
                  {loading || error
                    ? "--"
                    : value === "all"
                      ? batches.length
                      : batches.filter((batch) => batch.status === value)
                          .length}
                </span>
              </button>
            ))}
          </div>
          <div className={styles.search}>
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              aria-hidden="true"
            >
              <circle cx="10.5" cy="10.5" r="6.5" />
              <path d="m16 16 5 5" />
            </svg>
            <input
              type="search"
              aria-label="Search collections"
              placeholder="Search batch ID or image name"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setLimit(6);
              }}
            />
          </div>
        </div>
        {loading ? (
          <div className={styles.loading} role="status">
            Loading your collections...
          </div>
        ) : error ? (
          <section className={styles.empty} role="alert">
            <span className={shared.eyebrow}>LET'S TRY AGAIN</span>
            <h2>Collections could not be loaded</h2>
            <p>{error}</p>
            <button
              type="button"
              className={shared.primaryButton}
              onClick={() => setRetry((value) => value + 1)}
            >
              Try again
            </button>
          </section>
        ) : (
          <>
            <div className={styles.resultLine}>
              <span>
                {matching.length}{" "}
                {matching.length === 1 ? "collection" : "collections"}
                {search ? ` matching "${search}"` : ""}
              </span>
              <span>Newest first</span>
            </div>
            {matching.length ? (
              <div className={styles.cardList}>
                {matching.slice(0, limit).map((batch) => (
                  <CollectionCard key={batch.batchId} batch={batch} />
                ))}
              </div>
            ) : (
              <section className={styles.empty}>
                <span className={shared.eyebrow}>ROOM FOR SOMETHING NEW</span>
                <h2>
                  {batches.length
                    ? "No matching collections"
                    : "Your collection starts here"}
                </h2>
                <p>
                  {batches.length
                    ? "Try a different status or search to find your batch."
                    : "Create your first batch and bring your images together."}
                </p>
                {batches.length ? (
                  <button
                    type="button"
                    className={shared.primaryButton}
                    onClick={() => {
                      setFilter("all");
                      setSearch("");
                    }}
                  >
                    Clear filters
                  </button>
                ) : (
                  <Link
                    to="/crateBatch"
                    className={`${shared.primaryButton} ${styles.createLink}`}
                  >
                    Create a batch
                  </Link>
                )}
              </section>
            )}
            {limit < matching.length && (
              <div className={styles.loadMore}>
                <button
                  type="button"
                  className={shared.primaryButton}
                  onClick={() => setLimit((value) => value + 6)}
                >
                  Show more collections
                </button>
                <small>
                  Showing {Math.min(limit, matching.length)} of{" "}
                  {matching.length}
                </small>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
